import type { Influencer } from "@/marketing/models/influencer";
import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import {
  STANDARD_PIPELINE_DEFINITION,
  type CampaignPipelineDefinition,
} from "@/marketing/models/campaignPipeline";
import { getSocialMediaUrl } from "./socialMediaUrls";
import { formatDate } from "./formatters";
import { formatCreatorScore, SCORE_REVIEW_LABELS, type CreatorScoreSummary } from "@/marketing/models/creatorScore";
import { getStageLabel } from "./campaignPipeline";

export function escapeCsvField(value: string): string {
  const guardedValue = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  if (
    guardedValue.includes(",") ||
    guardedValue.includes('"') ||
    guardedValue.includes("\n") ||
    guardedValue.includes("\r")
  ) return `"${guardedValue.replace(/"/g, '""')}"`;

  return guardedValue;
}

export function generateInfluencerCsv(influencers: Influencer[]): string {
  const header = "Influencer Name,Social Media URL,Region,Active Email";
  const rows = influencers.map((inf) => {
    const name = inf.displayName || inf.handle;
    const url = getSocialMediaUrl(inf.platform, inf.handle);
    const region = inf.countrySignals?.[0] ?? "";
    const email = inf.emails?.[0] ?? "";
    return [name, url, region, email].map(escapeCsvField).join(",");
  });
  return [header, ...rows].join("\n");
}

export function generateCampaignInfluencerCsv(
  creators: CampaignInfluencer[],
  tagNameById: Map<string, string> = new Map(),
  scores?: Record<string, CreatorScoreSummary>,
  scoreAvailability: "ready" | "loading" | "unavailable" = "ready",
  definition: CampaignPipelineDefinition = STANDARD_PIPELINE_DEFINITION,
): string {
  const header =
    "Handle,Influencer ID,Platform,Social Media URL,Stage,Joined,Last Activity,Selected SKUs,Tags" +
    (scores ? ",Creator Score / 10,Review Status,Formula,Data As Of,Freshness,Score Availability" : "");
  const rows = creators.map((c) => {
    const stage = c.rejected
      ? "Rejected"
      : getStageLabel(definition, c.pipelineStage);
    const socialUrl =
      c.platform && c.handle ? getSocialMediaUrl(c.platform, c.handle) : "";
    const tagNames = (c.tags ?? [])
      .map((tagId) => tagNameById.get(tagId))
      .filter((name): name is string => Boolean(name));
    const score = c.userId == null ? undefined : scores?.[c.userId];
    return [
      c.handle ?? "",
      c.influencerId,
      c.platform ?? "",
      socialUrl,
      stage,
      formatDate(c.joinedAt),
      formatDate(c.lastTransitionAt),
      (c.selectedSkus ?? []).join("; "),
      tagNames.join("; "),
      ...(scores ? [
        score ? formatCreatorScore(score.finalScore).replace(" / 10", "") : "",
        score ? SCORE_REVIEW_LABELS[score.reviewStatus] : c.userId == null ? "Hub user not linked" : scoreAvailability === "unavailable" ? "Score unavailable" : scoreAvailability === "loading" ? "Loading score" : "",
        score?.formulaVersion ?? "",
        score?.inputCutoff ?? "",
        score?.freshness ?? "",
        scoreAvailability,
      ] : []),
    ]
      .map(escapeCsvField)
      .join(",");
  });
  return [header, ...rows].join("\n");
}

export function downloadCsv(content: string, filename: string): void {
  const blob = new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
